import { UUID } from '@cmg/common';
import {
  Alert,
  Box,
  Button,
  Grid,
  List,
  LoadingButton,
  Section,
  SnackbarManager,
  Stack,
} from '@cmg/design-system';
import { FormikSelectField } from '@cmg/design-system-formik';
import { Form, FormikProvider, useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { InvalidFormFieldsAlert } from '../../../../common/components/alerts/invalid-form-fields-alert/InvalidFormFieldsAlert';
import { IndicationNoteType } from '../../../../graphql';
import { updateDemandGridRowsReactiveVar } from '../../../order-book/institutional-demand/hooks/useUpdateDemandGridRowsReactiveVar';
import { useCmgEntityKeyContext } from '../../contexts/CmgEntityKeyContext';
import { OfferingSidePanelRouteContext } from '../../OfferingSidePanelContainer';
import offeringSidePanelSellSideRouteFactory from '../../offeringSidePanelSellSideRouteFactory';
import { useNotes_IndicationNoteQuery } from './graphql';
import { useAssignNoteMutation } from './hooks/useAssignNoteMutation';
import { useNoteSearchParams } from './hooks/useNoteSearchParams';
import Note from './Note';
import NoteForm, { FormType, SubmitNotesSchema } from './NoteForm';
import { filterOptions, NotesFilters } from './NotesModel.util';

export type Props = {
  offeringId: UUID;
  indicationId: UUID;
};

const Notes: React.FC<Props> = ({ offeringId, indicationId }) => {
  const { noteType } = useNoteSearchParams({ noteType: NotesFilters.ALL });
  const formik = useFormik({
    initialValues: { notesType: noteType },
    enableReinitialize: true,
    onSubmit: values => {
      handleNoteTypeChange(values.notesType);
    },
  });

  const navigate = useNavigate();
  /**
   * `Notes` is rendered under `OfferingSidePanelSellSideRoutes`' `useRoutes(routes, rest)` (see
   * useOfferingSidePanelMatch.ts), which scopes `useLocation()` for everything beneath it to the
   * panel-relative `rest` alone - dropping the host page's URL prefix and the real query string.
   * Navigating with that scoped pathname would escape the host route entirely, so read the real,
   * unscoped host prefix and cmgEntityKey from context instead - the same pattern `NotesRoute`
   * already uses for its own investor-switching navigation.
   */
  const baseSidePanelUrl = React.useContext(OfferingSidePanelRouteContext);
  const { cmgEntityKey } = useCmgEntityKeyContext();
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [isNewNote, setIsNewNote] = React.useState<boolean>(false);

  /**
   * `NotesRoute` reuses this component instance across investors (it isn't keyed by
   * `indicationId`), so a save started for one indication can resolve after the operator has
   * navigated to another. Track the latest indication via a ref (rather than relying on the
   * `indicationId` closed over by `onSubmit`) so the post-save continuation can detect that.
   */
  const indicationIdRef = useRef(indicationId);
  indicationIdRef.current = indicationId;

  /**
   * Read the investor key from a ref (rather than closing over `cmgEntityKey` at the time
   * `handleNoteTypeChange` was created) so a save that completes after the user has switched
   * investors navigates using the investor they're currently viewing, not a stale one from when
   * this callback instance was created.
   */
  const cmgEntityKeyRef = useRef(cmgEntityKey);
  cmgEntityKeyRef.current = cmgEntityKey;

  const {
    data: notesData,
    loading: notesLoading,
    refetch: refetchNotes,
  } = useNotes_IndicationNoteQuery({
    variables: { offeringId, indicationId },
    fetchPolicy: 'no-cache',
  });

  const [assignNote, { loading: assignNoteLoading }] = useAssignNoteMutation();

  const notes = notesData?.getInstitutionalIndicationNotes ?? [];
  const syndicateNote = notes.find(n => n.type === IndicationNoteType.SyndicateNote);
  const salesAndTradingNote = notes.find(n => n.type === IndicationNoteType.SalesAndTradingNote);

  const noteFormik = useFormik<FormType>({
    enableReinitialize: true,
    initialValues: {
      text:
        noteType === NotesFilters.SYNDICATE
          ? syndicateNote?.text ?? ''
          : salesAndTradingNote?.text ?? '',
    },
    validationSchema: SubmitNotesSchema,
    onSubmit: async values => {
      await onSubmit(values);
    },
    validateOnChange: true,
  });

  const { resetForm } = noteFormik;

  const handleNoteTypeChange = useCallback(
    (nextNoteType: NotesFilters) => {
      navigate(
        baseSidePanelUrl +
          offeringSidePanelSellSideRouteFactory.orderBookNotes.getUrlPath({
            cmgEntityKey: cmgEntityKeyRef.current,
            noteType: nextNoteType,
          })
      );
    },
    [navigate, baseSidePanelUrl]
  );

  const handleNoteChange = useCallback(
    (filterOption: NotesFilters) => {
      const currentNoteText =
        filterOption === NotesFilters.SYNDICATE ? syndicateNote?.text : salesAndTradingNote?.text;

      setIsNewNote(!currentNoteText);
      setEditMode(true);
      handleNoteTypeChange(filterOption);
    },
    [handleNoteTypeChange, salesAndTradingNote?.text, syndicateNote?.text]
  );

  const handleCancelEditMode = useCallback(() => {
    setEditMode(false);
    handleNoteTypeChange(NotesFilters.ALL);
    resetForm();
  }, [handleNoteTypeChange, resetForm]);

  const actionPanelTitle = useMemo(() => {
    if (!editMode) {
      return 'Notes';
    }
    if (isNewNote) {
      return 'Create New Note';
    }
    return 'Edit Note';
  }, [editMode, isNewNote]);

  const onSubmit = useCallback(
    async (values: FormType) => {
      const submittedIndicationId = indicationId;
      const submittedText = isEmpty(values.text) ? null : values.text;
      const isSyndicateNote = noteType === NotesFilters.SYNDICATE;

      try {
        const { data } = await assignNote({
          variables: {
            offeringId,
            indicationId,
            input: {
              text: submittedText,
              type: isSyndicateNote
                ? IndicationNoteType.SyndicateNote
                : IndicationNoteType.SalesAndTradingNote,
            },
          },
        });
        /**
         * `notesData` is fetched with `fetchPolicy: 'no-cache'`, so it isn't kept in sync by
         * Apollo's cache/refetchQueries machinery after a mutation — refetch it explicitly so
         * the notes list reflects what was just saved. The mutation above has already
         * succeeded at this point, so a failure here doesn't mean the save failed — it means
         * the list may be showing stale data, which we surface separately below instead of
         * letting it fall into the catch and report a save error.
         */
        let refetchFailed = false;
        try {
          await refetchNotes();
        } catch {
          refetchFailed = true;
        }

        /**
         * The operator may have navigated to a different investor while this save was in
         * flight. This instance is now showing that investor's (possibly in-progress, unsaved)
         * note, so don't close its edit mode or route away from it — the save already
         * succeeded server-side, there's just nothing left for this component to do for it.
         */
        if (indicationIdRef.current !== submittedIndicationId) {
          return;
        }

        setEditMode(false);
        handleNoteTypeChange(NotesFilters.ALL);
        updateDemandGridRowsReactiveVar({
          updated: [
            {
              id: indicationId,
              notes: data?.assignIndicationNote.reduce((acc, curr) => {
                switch (curr.type) {
                  case IndicationNoteType.SyndicateNote:
                    return { ...acc, syndicate: curr.text };
                  case IndicationNoteType.SalesAndTradingNote:
                    return { ...acc, salesAndTrading: curr.text };
                  case IndicationNoteType.SalesContactNote:
                    return { ...acc, salesContact: curr.text };
                  case IndicationNoteType.SalesDepartmentNote:
                    return { ...acc, salesDepartment: curr.text };
                  default:
                    return acc;
                }
              }, {}),
            },
          ],
        });
        if (refetchFailed) {
          SnackbarManager.info(
            'Note saved, but the notes list may be out of date. Refresh to see the latest.'
          );
        } else {
          SnackbarManager.success('Note Saved');
        }
      } catch {
        SnackbarManager.error('An error has occurred at saving the note');
      }
    },
    [assignNote, handleNoteTypeChange, indicationId, noteType, offeringId, refetchNotes]
  );

  return (
    <Stack data-testid="notes-section">
      <Alert severity="info">The following information is visible to your firm only</Alert>

      <Section
        data-testid="notes-action-panel-section"
        title={actionPanelTitle}
        pt={1}
        actions={[
          ...(editMode
            ? [
                <Button key="cancel" variant="outlined" onClick={handleCancelEditMode}>
                  Cancel
                </Button>,
                <LoadingButton
                  form="note-form"
                  type="submit"
                  variant="contained"
                  loading={notesLoading || assignNoteLoading}
                  key="save"
                >
                  Save
                </LoadingButton>,
              ]
            : []),
        ]}
      />
      <Box pb={2}>
        <InvalidFormFieldsAlert
          errors={noteFormik.errors}
          isSubmitting={noteFormik.isSubmitting}
          touched={noteFormik.touched}
          validationSchema={SubmitNotesSchema}
        />
      </Box>
      <Grid container spacing={2} pl={1}>
        <FormikProvider value={formik}>
          <Grid item xs={4}>
            <FormikSelectField
              aria-label="Select note type"
              name="notesType"
              label="Type"
              disabled={editMode}
              options={filterOptions}
              showHelperTextInTooltip
              fullWidth
              onChange={selected => {
                handleNoteTypeChange(selected!);
              }}
            />
          </Grid>
        </FormikProvider>
      </Grid>

      {!editMode && (
        <List disablePadding data-testid="notes-list">
          {(noteType === NotesFilters.ALL || noteType === NotesFilters.SYNDICATE) && (
            <Note
              title="Syndicate"
              text={syndicateNote?.text ?? 'No Syndicate notes have been entered yet.'}
              filterType={NotesFilters.SYNDICATE}
              onChange={handleNoteChange}
              testId="syndicate-notes-list"
            />
          )}
          {(noteType === NotesFilters.ALL || noteType === NotesFilters.SALES_TRADER) && (
            <Note
              title="Sales & Trading"
              text={salesAndTradingNote?.text ?? 'No Sales & Trading notes have been entered yet.'}
              filterType={NotesFilters.SALES_TRADER}
              onChange={handleNoteChange}
              testId="sales-and-trading-notes-list"
            />
          )}
        </List>
      )}
      {editMode && (
        <FormikProvider value={noteFormik}>
          <Form id="note-form" noValidate data-testid="note-form">
            <NoteForm />
          </Form>
        </FormikProvider>
      )}
    </Stack>
  );
};

export default Notes;
